import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExportMenu, { ExportDataMap } from './ExportMenu';

export enum ExportEntity {
  Users = 'Users',
  OnboardingUsers = 'Onboarding Users',
  Permissions = 'Permissions',
  Roles = 'Roles',
  Groups = 'Groups',
  DisplayedUsers = 'Displayed Users',
  AllUsers = 'All Users',
  DisplayedPendingUsers = 'Displayed Pending Users',
  AllPendingUsers = 'All Pending Users',
}

export enum ExportFilePrefix {
  Users = 'users',
  OnboardingUsers = 'onboarding-users',
  Permissions = 'permissions',
  Roles = 'roles',
  Groups = 'groups',
  DisplayedUsers = 'Displayed Users',
  AllUsers = 'All Users',
  DisplayedPendingUsers = 'Displayed Pending Users',
  AllPendingUsers = 'All Pending Users',
}

const PREFIX = 'CommonToolbarWithSearchBar';

const classes = {
  container: `${PREFIX}-container`,
  create: `${PREFIX}-create`,
  toolbar: `${PREFIX}-toolbar`,
  search: `${PREFIX}-search`,
  actions: `${PREFIX}-actions`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.search}`]: {
    flexGrow: 1,
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  [`& .${classes.create}`]: {
    minWidth: '140px',
  },
}));

interface CommonToolbarWithSearchBarProps {
  label: string;
  button: React.ReactNode;
  placeholder: string;
  searchText: string;
  onSearchTextChange: (val: string) => void;
  onCreate: () => void;
  exportDataMap?: ExportDataMap;
  exportFilenamePrefixMap?: { [label: string]: string };
  disabled?: boolean;
}

export default function CommonToolbarWithSearchBar({
  button,
  label,
  placeholder,
  searchText,
  exportDataMap,
  exportFilenamePrefixMap,
  disabled,
  onSearchTextChange = () => {},
  onCreate,
}: CommonToolbarWithSearchBarProps) {
  const onCreateClicked = (evt: React.MouseEvent) => {
    evt.preventDefault();
    onCreate();
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.toolbar}>
        <TextSearch
          id="text-search"
          icon={<SearchIcon />}
          className={classes.search}
          label={label}
          placeholder={placeholder}
          currentSearchFilter={searchText}
          onSearchChange={onSearchTextChange}
        />
        <Box className={classes.actions}>
          {button && (
            <Button
              className={classes.create}
              startIcon={<AddCircleOutlineIcon />}
              variant="contained"
              color="primary"
              onClick={onCreateClicked}>
              {button}
            </Button>
          )}
          {exportDataMap && (
            <ExportMenu
              dataMap={exportDataMap}
              filenamePrefixMap={exportFilenamePrefixMap ?? {}}
              disabled={disabled}
            />
          )}
        </Box>
      </Box>
    </StyledBox>
  );
}
